import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { LoadingList, ErrorState, EmptyState } from "./StateViews";

interface DomainListPageProps<T> {
  title: string;
  onOpenSearch: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  onOpenFilterSheet: () => void;
  filterActive: boolean;
  filterSummary: string;
  items: T[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  emptyTitle: string;
  emptyHint?: string;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export function DomainListPage<T>({
  title,
  onOpenSearch,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onOpenFilterSheet,
  filterActive,
  filterSummary,
  items,
  loading,
  error,
  onRetry,
  emptyTitle,
  emptyHint,
  getKey,
  renderItem,
  hasMore,
  onLoadMore,
  loadingMore,
}: DomainListPageProps<T>) {
  return (
    <section className="list-page" aria-labelledby="list-page-title">
      <header className="page-header">
        <h1 id="list-page-title" className="page-header__title">
          {title}
        </h1>
        <button type="button" className="icon-btn icon-btn--sm" onClick={onOpenSearch} aria-label="جست‌وجوی سراسری">
          <Icon name="search" width={15} height={15} aria-hidden="true" />
        </button>
      </header>

      <div className="list-toolbar">
        <label className="search-field">
          <Icon name="search" width={15} height={15} aria-hidden="true" />
          <input
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </label>
        <button
          type="button"
          className="filter-trigger"
          data-active={filterActive}
          onClick={onOpenFilterSheet}
          aria-haspopup="dialog"
        >
          فیلتر <span aria-hidden="true">⌄</span>
        </button>
      </div>

      <p className="filter-summary">{filterSummary}</p>

      {error && <ErrorState message={error} onRetry={onRetry} />}
      {!error && loading && !items && <LoadingList />}
      {!error && !loading && items && items.length === 0 && (
        <EmptyState message={emptyTitle} hint={emptyHint} />
      )}

      {items && items.length > 0 && (
        <ul className="item-list">
          {items.map((item) => (
            <li key={getKey(item)}>{renderItem(item)}</li>
          ))}
        </ul>
      )}

      {hasMore && onLoadMore && (
        <button type="button" className="list-page__more" onClick={onLoadMore} disabled={loadingMore}>
          {loadingMore ? "در حال بارگذاری…" : "نمایش بیشتر"}
        </button>
      )}
    </section>
  );
}
